// constants
import {
  ACTIVATOR_KEY_REGEX,
  REPEAT_INTERVAL_IN_MS
} from './ShortcutProvider.consts';

// types
import type {
  ShortcutAction,
  ShortcutActivatorKey,
  ShortcutControllerKey,
  ShortcutKeySet,
  ShortcutStore
} from './ShortcutProvider.types';

// utils
import { isPrimaryModifier } from '../Typography/Kbd/Kbd.utils';

export const isValidActivatorKey = (keyCode: string) => {
  return ACTIVATOR_KEY_REGEX.test(keyCode);
};

export const isTargetInMuiPopoper = (target: HTMLElement | null) => {
  // Check if the event target is within a Popover, Modal, or Menu
  // Specifically for the MUI components popover when connecting a calendar entity
  return (
    target?.closest('.MuiPopover-root') ||
    target?.closest('.MuiModal-root') ||
    target?.closest('.MuiMenu-root')
  );
};

export const extractKeySet = (
  event: KeyboardEvent
): ShortcutKeySet | undefined => {
  // this occasionally happens when a user clicks immediately
  // after a reload. Its not clear why a click triggers a
  // keyboard event, but this guard prevents further issues.
  if (!event.key) return;

  const activatorKey = event.key.toUpperCase() as ShortcutActivatorKey;

  if (!isValidActivatorKey(activatorKey)) return;

  const controllerKeys = [
    ...(event.shiftKey ? ['SHIFT'] : []),
    ...(isPrimaryModifier(event) ? ['PRIMARY'] : [])
  ] as ShortcutControllerKey[];

  return { activatorKey, controllerKeys };
};

/**
 * Canonical identifier for a key combination, so two combinations can be compared
 * regardless of the order their controller keys were declared in.
 */
export const keySetId = (
  activatorKey: ShortcutActivatorKey,
  controllerKeys: ShortcutControllerKey[]
) => [...[...controllerKeys].sort(), activatorKey].join('+');

/**
 * Fingerprint of everything a shortcut set stores in the registry *except* its callbacks.
 *
 * `useCreateShortcutContext` uses this as its effect dependency, so registration only
 * happens again when a shortcut is added, removed, rebound, relabelled or toggled — and
 * not when a caller re-creates its callbacks, which happens on every render.
 *
 * Serialised as JSON tuples so that a name, label or description containing the separator
 * can never forge a field boundary and collide with a different set.
 */
export const shortcutSetFingerprint = (shortcuts: ShortcutAction[]) =>
  JSON.stringify(
    shortcuts.map(
      ({
        name,
        label,
        description,
        activatorKey,
        controllerKeys,
        active,
        hidden,
        repeatable
      }) => [
        name,
        label,
        description,
        keySetId(activatorKey, controllerKeys),
        active !== false,
        !!hidden,
        !!repeatable
      ]
    )
  );

/**
 * Operations on the provider's store.
 *
 * They live here rather than inside `ShortcutProvider` because none of them closes over a
 * reactive value: the store arrives as an argument. That is what leaves the provider's
 * effects with honest dependency arrays instead of a list of functions to memoize.
 */

/** Every shortcut in a context that the narrowings in place have not switched off. */
export const getActiveShortcuts = ({
  contexts
}: ShortcutStore): ShortcutAction[] =>
  Object.values(contexts)
    .filter(({ isActive }) => isActive)
    .flatMap(({ shortcuts }) => Object.values(shortcuts));

// Every narrowing in place applies: a context stays active only if all of them allow it.
// Deliberately not "the innermost one wins" -- React commits child effects before parent
// effects, so an outer owner that starts narrowing in the same commit as a nested one would
// land last and hand back what the nested owner just took away. Intersecting makes the
// result independent of the order the narrowings arrived in.
const applyNarrowings = (store: ShortcutStore) => {
  const activeContexts = store.narrowings.reduce(
    (allowed, { contexts }) =>
      allowed.filter((context) => contexts.includes(context)),
    Object.keys(store.contexts)
  );

  Object.entries(store.contexts).forEach(([name, context]) => {
    context.isActive = activeContexts.includes(name);
  });
};

// Contexts live for as long as the provider does. Deleting them when their last
// shortcut goes away would reset the `isActive` flag the narrowings manage, and would
// let one component wipe a context it shares with another.
const ensureContext = (store: ShortcutStore, name: string) => {
  if (!store.contexts[name]) {
    store.contexts[name] = { name, isActive: true, shortcuts: {} };

    // A context registered while an owner holds a narrowing must not escape it by being
    // born active -- which the provider's own help shortcut does, since it registers from
    // an effect and effects of children commit first.
    applyNarrowings(store);
  }

  return store.contexts[name];
};

export const addNarrowing = (
  store: ShortcutStore,
  id: string,
  contexts: string[]
) => {
  store.narrowings = [
    ...store.narrowings.filter((narrowing) => narrowing.id !== id),
    { id, contexts }
  ];

  applyNarrowings(store);
};

export const removeNarrowing = (store: ShortcutStore, id: string) => {
  store.narrowings = store.narrowings.filter(
    (narrowing) => narrowing.id !== id
  );

  applyNarrowings(store);
};

const findShortcutForKeySet = (store: ShortcutStore, set: ShortcutKeySet) => {
  const wantedKeySetId = keySetId(set.activatorKey, set.controllerKeys);

  return getActiveShortcuts(store).find(
    ({ active, activatorKey, controllerKeys }) =>
      active && keySetId(activatorKey, controllerKeys) === wantedKeySetId
  );
};

export const setShortcutsActive = (
  store: ShortcutStore,
  isActive: boolean,
  context: string,
  name?: string
) => {
  const shortcutContext = store.contexts[context];
  if (!shortcutContext) return;

  if (name) {
    if (!shortcutContext.shortcuts[name]) return;
    shortcutContext.shortcuts[name].active = isActive;
    return;
  }

  Object.values(shortcutContext.shortcuts).forEach((shortcut) => {
    shortcut.active = isActive;
  });
};

export const setShortcut = (
  store: ShortcutStore,
  {
    context,
    active = true,
    name,
    label,
    callback,
    description = '',
    activatorKey,
    controllerKeys,
    hidden = false,
    repeatable = false
  }: ShortcutAction & { context: string }
) => {
  if (!context) return;

  ensureContext(store, context).shortcuts[name] = {
    active,
    callback,
    name,
    label,
    description,
    activatorKey,
    controllerKeys,
    hidden,
    repeatable
  };
};

export const removeShortcuts = (
  store: ShortcutStore,
  context: string,
  name?: string
) => {
  const shortcutContext = store.contexts[context];
  if (!shortcutContext) return;

  if (name) {
    delete shortcutContext.shortcuts[name];
    return;
  }

  shortcutContext.shortcuts = {};
};

export const handleKeyDown = (store: ShortcutStore, event: KeyboardEvent) => {
  const target = event.target as HTMLElement;
  if (!target?.isConnected) return;

  if (isTargetInMuiPopoper(target)) return;

  const keySet = extractKeySet(event);
  if (!keySet) return;

  // Check if user is typing in an input field
  const tagName = target.tagName.toUpperCase();
  const isInputElement =
    tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable;

  // Determine if this is a non-modifier shortcut
  // Non-modifier shortcuts: plain keys or Shift+key combinations.
  // Shift-only shortcuts are excluded because Shift is primarily for typing
  // uppercase/special characters (e.g., '?', '!') rather than shortcuts.
  const isNonModifierShortcut =
    keySet.controllerKeys.length === 0 ||
    (keySet.controllerKeys.length === 1 &&
      keySet.controllerKeys[0] === 'SHIFT');

  // Block non-modifier shortcuts when typing in input fields
  if (isInputElement && isNonModifierShortcut) return;

  const shortcut = findShortcutForKeySet(store, keySet);
  if (!shortcut) return;

  // Holding a key down makes the browser repeat keydown. Most shortcuts should fire once
  // per press -- repeating "open the appointment form" is never wanted -- so a repeat is
  // dropped unless the shortcut opts in with `repeatable`, which is what lets the arrow
  // keys keep moving through calendar cells while held.
  if (event.repeat && !shortcut.repeatable) return;

  // Claimed either way: swallowing the key without this lets the browser scroll the
  // calendar on an arrow press that only got throttled.
  event.preventDefault();
  event.stopPropagation();

  // Repeats are throttled to the rate a held key used to move at. Native repeat is
  // roughly three times faster, which outruns the re-render each move causes. Only
  // repeatable shortcuts keep a timestamp, so a one-shot shortcut firing cannot swallow
  // the repeat of a key held right after it.
  if (shortcut.repeatable) {
    const pressed = keySetId(keySet.activatorKey, keySet.controllerKeys);
    const now = Date.now();

    if (
      event.repeat &&
      now - (store.lastFiredAt[pressed] ?? 0) < REPEAT_INTERVAL_IN_MS
    ) {
      return;
    }

    store.lastFiredAt[pressed] = now;
  }

  shortcut.callback();
};
